import { supabase } from '../supabase-client';
import type { Database } from '../types/database';

export type ClientDocument = Database['public']['Tables']['client_documents']['Row'];
export type ClientDocumentInsert = Database['public']['Tables']['client_documents']['Insert'];

/**
 * Assign a document directly to a client
 */
export async function assignDocumentToClient(
  clientId: string,
  documentId: string,
  assignedBy: string,
  notes?: string
) {
  const { data, error } = await supabase
    .from('client_documents')
    .insert({
      client_id: clientId,
      document_id: documentId,
      assigned_by: assignedBy,
      assignment_type: 'direct',
      notes,
    })
    .select(
      `
      *,
      documents!inner(
        id,
        title,
        document_type,
        country,
        status,
        created_at
      )
    `
    )
    .single();

  if (error) throw error;
  return data;
}

/**
 * Remove a document assignment from a client
 */
export async function removeDocumentFromClient(
  clientId: string,
  documentId: string
) {
  const { error } = await supabase
    .from('client_documents')
    .delete()
    .eq('client_id', clientId)
    .eq('document_id', documentId)
    .eq('assignment_type', 'direct');

  if (error) throw error;
}

/**
 * Get all documents for a client (direct + from cases)
 */
export async function getClientDocuments(clientId: string) {
  const { data, error } = await supabase
    .from('client_documents')
    .select(
      `
      *,
      documents!inner(
        id,
        title,
        document_type,
        country,
        status,
        content,
        created_at,
        updated_at
      ),
      profiles!client_documents_assigned_by_fkey(
        full_name,
        email
      ),
      cases(
        id,
        title,
        case_number
      )
    `
    )
    .eq('client_id', clientId)
    .order('assigned_at', { ascending: false });

  if (error) throw error;
  return data;
}

/**
 * Get client documents filtered by type
 */
export async function getClientDocumentsByType(
  clientId: string,
  documentType: string
) {
  const { data, error } = await supabase
    .from('client_documents')
    .select(
      `
      *,
      documents!inner(
        id,
        title,
        document_type,
        country,
        status,
        content,
        created_at
      )
    `
    )
    .eq('client_id', clientId)
    .eq('documents.document_type', documentType)
    .order('assigned_at', { ascending: false });

  if (error) throw error;
  return data;
}

/**
 * Get client documents filtered by assignment type
 */
export async function getClientDocumentsByAssignmentType(
  clientId: string,
  assignmentType: 'direct' | 'from_case'
) {
  const { data, error } = await supabase
    .from('client_documents')
    .select(
      `
      *,
      documents!inner(
        id,
        title,
        document_type,
        country,
        status,
        content,
        created_at
      ),
      cases(
        id,
        title,
        case_number
      )
    `
    )
    .eq('client_id', clientId)
    .eq('assignment_type', assignmentType)
    .order('assigned_at', { ascending: false });

  if (error) throw error;
  return data;
}

/**
 * Check if a document is already assigned to a client
 */
export async function isDocumentAssignedToClient(
  clientId: string,
  documentId: string
): Promise<boolean> {
  const { data, error } = await supabase
    .from('client_documents')
    .select('id')
    .eq('client_id', clientId)
    .eq('document_id', documentId)
    .eq('assignment_type', 'direct')
    .maybeSingle();

  if (error) throw error;
  return !!data;
}

/**
 * Get document count for a client
 */
export async function getClientDocumentCount(clientId: string) {
  const { count, error } = await supabase
    .from('client_documents')
    .select('*', { count: 'exact', head: true })
    .eq('client_id', clientId);

  if (error) throw error;
  return count || 0;
}

/**
 * Get documents grouped by type for a client
 */
export async function getClientDocumentsGroupedByType(clientId: string) {
  const { data, error } = await supabase
    .from('client_documents')
    .select(
      `
      *,
      documents!inner(
        id,
        title,
        document_type,
        country,
        status,
        created_at
      )
    `
    )
    .eq('client_id', clientId)
    .order('assigned_at', { ascending: false });

  if (error) throw error;

  // Group by document type
  const grouped: Record<string, typeof data> = {};
  data.forEach((item) => {
    const doc = item.documents as any;
    const type = doc.document_type;
    if (!grouped[type]) {
      grouped[type] = [];
    }
    grouped[type].push(item);
  });

  return grouped;
}

/**
 * Get recent document assignments for a client
 */
export async function getRecentClientDocuments(
  clientId: string,
  limit: number = 5
) {
  const { data, error } = await supabase
    .from('client_documents')
    .select(
      `
      *,
      documents!inner(
        id,
        title,
        document_type,
        country,
        status,
        created_at
      )
    `
    )
    .eq('client_id', clientId)
    .order('assigned_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data;
}

